/**
 * TSOGN offline layer.
 *
 * Methodology principle: User data is portable and exportable anytime.
 * IndexedDB holds the authoritative copy of the user's Notepad and My Roots
 * while they work; the server is a sync target, not a source of truth. If
 * the server dies, the subscription expires, the account is suspended —
 * the user still has their work.
 *
 * Schema:
 *   notes          key=id      → Notepad documents (section-tagged)
 *   family         key=id      → My Roots ancestors
 *   sync_queue     key=uuid    → queued mutations to flush on reconnect
 *   failed_queue   key=uuid    → mutations that exhausted retries; preserved
 *                                 so the user's evidence is never silently lost
 *   meta           key=string  → arbitrary metadata (last_sync, etc.)
 */
import { openDB } from 'idb';

const DB_NAME = 'tsogn-offline';
const DB_VERSION = 2;

let dbPromise = null;

export function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains('notes')) {
          db.createObjectStore('notes', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('family')) {
          db.createObjectStore('family', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('sync_queue')) {
          const q = db.createObjectStore('sync_queue', { keyPath: 'id' });
          q.createIndex('by_created', 'created_at');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
        if (oldVersion < 2 && !db.objectStoreNames.contains('failed_queue')) {
          const f = db.createObjectStore('failed_queue', { keyPath: 'id' });
          f.createIndex('by_failed', 'failed_at');
        }
      },
    });
  }
  return dbPromise;
}

/* ─── Notes ─── */
export async function cacheNote(note) {
  const db = await getDB();
  await db.put('notes', { ...note, _cached_at: Date.now() });
}

export async function cacheNotes(notes) {
  const db = await getDB();
  const tx = db.transaction('notes', 'readwrite');
  await Promise.all(notes.map((n) => tx.store.put({ ...n, _cached_at: Date.now() })));
  await tx.done;
}

export async function getCachedNote(id) {
  const db = await getDB();
  return db.get('notes', id);
}

export async function getCachedNotes() {
  const db = await getDB();
  return db.getAll('notes');
}

export async function removeCachedNote(id) {
  const db = await getDB();
  await db.delete('notes', id);
}

/* ─── Family / Ancestors ─── */
export async function cacheFamily(members) {
  const db = await getDB();
  const tx = db.transaction('family', 'readwrite');
  await tx.store.clear();
  await Promise.all(members.map((m) => tx.store.put({ ...m, _cached_at: Date.now() })));
  await tx.done;
}

export async function getCachedFamily() {
  const db = await getDB();
  return db.getAll('family');
}

export async function upsertCachedFamilyMember(member) {
  const db = await getDB();
  await db.put('family', { ...member, _cached_at: Date.now() });
}

/* ─── Sync queue ─── */
export async function enqueueMutation(mutation) {
  /* mutation shape:
   *   {
   *     id:       uuid,
   *     method:   'POST'|'PUT'|'PATCH'|'DELETE',
   *     path:     '/api/notes/123/autosave',
   *     body:     serializable,
   *     kind:     'note_autosave' | 'family_create' | 'family_update' | etc.,
   *     created_at: iso,
   *     attempts: 0
   *   }
   */
  const db = await getDB();
  const full = {
    ...mutation,
    id: mutation.id || crypto.randomUUID(),
    created_at: mutation.created_at || new Date().toISOString(),
    attempts: mutation.attempts || 0,
  };
  await db.put('sync_queue', full);
  return full;
}

export async function listQueued() {
  const db = await getDB();
  return db.getAllFromIndex('sync_queue', 'by_created');
}

export async function dequeueMutation(id) {
  const db = await getDB();
  await db.delete('sync_queue', id);
}

export async function markAttempt(id) {
  const db = await getDB();
  const m = await db.get('sync_queue', id);
  if (m) {
    m.attempts = (m.attempts || 0) + 1;
    m.last_attempt_at = new Date().toISOString();
    await db.put('sync_queue', m);
  }
}

export async function queueSize() {
  const db = await getDB();
  return db.count('sync_queue');
}

/* ─── Failed queue ─── preserve exhausted mutations so evidence is never dropped */
export async function moveToFailed(mutation, failureReason) {
  const db = await getDB();
  const doc = {
    ...mutation,
    failed_at: new Date().toISOString(),
    failure_reason: failureReason || 'unknown',
  };
  const tx = db.transaction(['sync_queue', 'failed_queue'], 'readwrite');
  await tx.objectStore('failed_queue').put(doc);
  await tx.objectStore('sync_queue').delete(mutation.id);
  await tx.done;
}

export async function listFailed() {
  const db = await getDB();
  return db.getAll('failed_queue');
}

export async function failedCount() {
  const db = await getDB();
  return db.count('failed_queue');
}

export async function clearFailed() {
  const db = await getDB();
  await db.clear('failed_queue');
}

export async function requeueFailed(id) {
  const db = await getDB();
  const tx = db.transaction(['sync_queue', 'failed_queue'], 'readwrite');
  const doc = await tx.objectStore('failed_queue').get(id);
  if (doc) {
    const { failed_at, failure_reason, ...rest } = doc;
    rest.attempts = 0;
    await tx.objectStore('sync_queue').put(rest);
    await tx.objectStore('failed_queue').delete(id);
  }
  await tx.done;
}

/* ─── Meta ─── */
export async function setMeta(key, value) {
  const db = await getDB();
  await db.put('meta', { key, value, updated_at: new Date().toISOString() });
}

export async function getMeta(key) {
  const db = await getDB();
  const row = await db.get('meta', key);
  return row?.value;
}

/* ─── Data export — user-facing "take your data" bundle ─── */
export async function exportOfflineData() {
  const [notes, family, queue, failed] = await Promise.all([
    getCachedNotes(),
    getCachedFamily(),
    listQueued(),
    listFailed(),
  ]);
  return {
    exported_at: new Date().toISOString(),
    source: 'tsogn-offline-cache',
    notes,
    family,
    pending_sync: queue,
    failed_sync: failed,
  };
}
